import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileText,
  Printer,
  ArrowLeft,
  ClipboardCheck,
  FileSignature,
  User,
  Building2,
  AlertCircle,
  Save,
  Download,
  Loader2,
  Archive,
  PlusCircle,
  Search,
  Trash2,
  Eye,
  Pencil,
  Calendar,
  CheckCircle2,
  UserCheck,
  Briefcase,
  Shield,
  X,
  List,
  LayoutGrid,
  Filter,
  ChevronRight
} from "lucide-react";
import api from "./api";
import DynamicFormRenderer from "./components/DynamicFormRenderer";
import MandatoPraticaForm from "./components/MandatoPraticaForm";
import PreventivoForm from "./components/PreventivoForm";
import SafeBoundary from "./components/SafeBoundary";

/**
 * STABILITY FIX: Stable error message component that always renders a container
 * This prevents React DOM reconciliation errors from conditional rendering
 */
function FieldError({ error }) {
  return (
    <div className="min-h-[20px]">
      {error ? (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * STABILITY FIX: Stable info message component  
 */
function InfoMessage({ show, children, className = "" }) {
  return (
    <div className={`min-h-[20px] ${className}`}>
      {show ? children : null}
    </div>
  );
}

// Lista moduli disponibili (template system)
const availableForms = [
  {
    id: "presa-in-carico",
    name: "Mandato Pratica",
    description: "Modulo di mandato per l'avvio di una pratica con firma del cliente",
    icon: ClipboardCheck,
    category: "Pratiche"
  },
  {
    id: "delega",
    name: "Delega",
    description: "Autorizzazione per delega a terzi per operazioni e pratiche",
    icon: UserCheck,
    category: "Autorizzazioni"
  },
  {
    id: "incarico-professionale",
    name: "Incarico Professionale",
    description: "Conferimento incarico di consulenza del lavoro con condizioni e compensi",
    icon: Briefcase,
    category: "Contratti"
  },
  {
    id: "consenso-privacy",
    name: "Consenso Privacy",
    description: "Informativa e raccolta consensi GDPR per il trattamento dei dati personali",
    icon: Shield,
    category: "Compliance"
  },
  {
    id: "preventivo-pratica",
    name: "Preventivo Pratica",
    description: "Preventivo professionale per pratica/servizio, con workflow Bozza→Inviato→Accettato e conversione automatica in pratica operativa",
    icon: FileText,
    category: "Pratiche"
  }
];

/**
 * Infer branding context from practice type.
 * - "Gestionale Agenzia" or practice types containing "GA" or "Agenzia" → "agenzia"
 * - Everything else → "studio"
 */
function inferBrandingFromPracticeType(practiceType) {
  if (!practiceType) return "studio";
  const normalized = practiceType.toLowerCase();
  if (normalized.includes("agenzia") || normalized.includes("gestionale agenzia") || normalized === "ga") {
    return "agenzia";
  }
  return "studio";
}

// Main Modulistica Component
function Modulistica({
  clients = [],
  operators = [],
  currentOperator = null,
  cases = [],
  onOpenClientDialog = null,
  initialFormType = null,
  onInitialFormConsumed = null,
  configTables = { practice_types: [], ente_options: [] },
  // 14/07/2026 — Prop per riorganizzazione Gestionale Pratiche.
  // Quando `true` (usato dentro Gestionale Pratiche → sub-tab Preventivi):
  //   - Galleria template mostra SOLO "preventivo-pratica"
  //   - Archivio mostra SOLO i preventivi salvati
  // Quando `false` (Modulistica classica, sezione documenti):
  //   - Il template "preventivo-pratica" è nascosto dalla galleria
  //   - I preventivi sono esclusi dall'archivio
  // Fonte unica di verità: no duplicazioni, no divergenze.
  restrictToPreventivi = false,
  // 14/07/2026 — Deep-link "apri preventivo esistente" (Pratica →
  // Preventivo bidirezionale). Se settato, apriamo il form del
  // preventivo in edit mode al mount. Consumato via callback
  // per evitare re-apertura ai render successivi.
  initialEditFormId = null,
  onInitialEditFormConsumed = null,
}) {
  const [activeView, setActiveView] = useState("templates"); // "templates" | "archive" | "form"
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [editingForm, setEditingForm] = useState(null);
  const [savedForms, setSavedForms] = useState([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);

  // Quick-action deep-link: se App.js passa `initialFormType` (es. dal
  // pulsante "+ Nuovo Preventivo" del Cruscotto), apriamo direttamente
  // il form senza tornare alla lista template. Una sola volta per
  // navigazione (consumiamo subito il flag via callback).
  useEffect(() => {
    if (initialFormType) {
      setSelectedFormType(initialFormType);
      setEditingForm(null);
      setActiveView("form");
      if (typeof onInitialFormConsumed === "function") onInitialFormConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormType]);

  // 14/07/2026 — Deep-link EDIT: apre in edit mode un modulo esistente
  // (usato per il back-link Pratica → Preventivo). Fetch on-demand via
  // `api.getForm` per garantire dati freschi. Consumato una-sola-volta.
  useEffect(() => {
    if (!initialEditFormId) return;
    let cancelled = false;
    api.getForm(initialEditFormId)
      .then((form) => {
        if (cancelled || !form) return;
        setSelectedFormType(form.formType || "preventivo-pratica");
        setEditingForm(form);
        setActiveView("form");
      })
      .catch(() => { /* silent */ })
      .finally(() => {
        if (typeof onInitialEditFormConsumed === "function") onInitialEditFormConsumed();
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditFormId]);
  
  // Filters for archive
  const [filterFormType, setFilterFormType] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  // 21/07/2026 · v1.4.1 fix — Default management filter: "all" per
  // TUTTE le modalità (Modulistica classica e sub-tab Preventivi).
  // Precedentemente in modalità Preventivi il default era "agenzia" —
  // conseguenza: i preventivi di clienti con managementType="studio"
  // (o senza cliente/senza campo) venivano nascosti al primo accesso
  // all'archivio, portando l'utente a vedere "6 di 10" preventivi
  // senza capire dove fossero gli altri. L'utente ora deve
  // esplicitamente selezionare "Gestione Agenzia" se lo desidera.
  const [filterManagementType, setFilterManagementType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  // 15/07/2026 — Filtro rapido stato preventivo (chip cliccabili sopra
  // l'archivio). Attivo solo in modalità restrictToPreventivi. "convertito"
  // è uno stato derivato: presenza di `convertedPracticeId`.
  const [filterQuoteStatus, setFilterQuoteStatus] = useState("all");
  // 22/07/2026 · v1.8.0 — Filtro "Solo miei" preventivi. Attivo solo
  // in modalità restrictToPreventivi. Match su `form.operatorId ===
  // currentOperator.id` (fallback su `userId` per preventivi legacy
  // creati prima che l'operatorId fosse denormalizzato).
  // Persistito in sessionStorage così che navigando via tab e
  // tornando l'operatore mantenga la scelta corrente.
  const ONLY_MINE_STORAGE_KEY = "gis.preventivi.onlyMine";
  const [filterOnlyMine, setFilterOnlyMine] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return sessionStorage.getItem(ONLY_MINE_STORAGE_KEY) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { sessionStorage.setItem(ONLY_MINE_STORAGE_KEY, filterOnlyMine ? "1" : "0"); }
    catch { /* storage disabled */ }
  }, [filterOnlyMine]);

  // Load saved forms
  const loadSavedForms = useCallback(async () => {
    try {
      setIsLoadingForms(true);
      const filters = {};
      if (filterFormType !== "all") filters.formType = filterFormType;
      if (filterClient !== "all") filters.clientId = filterClient;
      const forms = await api.getForms(filters);
      setSavedForms(forms);
    } catch (error) {
      console.error("Error loading forms:", error);
    } finally {
      setIsLoadingForms(false);
    }
  }, [filterFormType, filterClient]);

  useEffect(() => {
    // Carica i form al mount E ad ogni cambio filtri/view (28/02/2026).
    // Prima si caricavano solo quando si apriva la tab "Archivio", quindi
    // il badge nel tab "Archivio (N)" mostrava sempre 0 finché l'utente
    // non la cliccava — bug segnalato dall'ufficio. Adesso il counter
    // riflette il numero reale di documenti archiviati fin dal primo
    // render della Modulistica.
    loadSavedForms();
  }, [loadSavedForms]);

  // Handle opening a form template
  const handleOpenForm = (formId) => {
    setSelectedFormType(formId);
    setEditingForm(null);
    setActiveView("form");
  };

  // Handle editing an existing form
  const handleEditForm = (form) => {
    setSelectedFormType(form.formType);
    setEditingForm(form);
    setActiveView("form");
  };

  // Handle form saved
  const handleFormSaved = () => {
    setActiveView("archive");
    loadSavedForms();
  };

  // Handle delete form
  const handleDeleteForm = async (formId) => {
    if (!window.confirm("Eliminare questo modulo salvato?")) return;
    
    try {
      await api.deleteForm(formId);
      setSavedForms(prev => prev.filter(f => f.id !== formId));
    } catch (error) {
      alert("Errore durante l'eliminazione");
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async (formId) => {
    try {
      await api.downloadFormPDF(formId);
    } catch (error) {
      alert(error.message || "Errore durante il download");
    }
  };

  // Check if any filter is active
  const hasActiveFilters = searchQuery || filterFormType !== "all" || filterClient !== "all" || filterManagementType !== "all" || filterQuoteStatus !== "all" || filterOnlyMine || dateRange.from || dateRange.to;

  // View mode for templates (compact list vs cards)
  const [templateViewMode, setTemplateViewMode] = useState("list"); // "list" or "cards"
  
  // Category filter for templates
  const [filterCategory, setFilterCategory] = useState("all");
  
  // Get unique categories
  const categories = [...new Set(availableForms.map(f => f.category))];

  // Sort templates alphabetically A→Z by visible name (22/02/2026,
  // richiesta utente). Solo ordering display: non tocca id/template
  // backend né l'ordine in `availableForms`. `localeCompare` con `it`
  // per ordinamento italiano (accenti, maiuscole/minuscole insensitive).
  // 14/07/2026 — Filtro secondo modalità Gestionale Pratiche / Modulistica.
  const visibleForms = availableForms.filter((f) =>
    restrictToPreventivi ? f.id === "preventivo-pratica" : f.id !== "preventivo-pratica"
  );
  const sortedTemplates = [...visibleForms].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" })
  );

  // Filter forms by category
  const filteredTemplates = filterCategory === "all"
    ? sortedTemplates
    : sortedTemplates.filter(f => f.category === filterCategory);

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setFilterFormType("all");
    setFilterClient("all");
    setFilterManagementType("all");
    setDateRange({ from: null, to: null });
    setFilterQuoteStatus("all");
    setFilterOnlyMine(false);
  };

  // Filter saved forms (client-side filtering for search and date range)
  const filteredForms = savedForms.filter(form => {
    // 14/07/2026 — Hard filter per la nuova organizzazione:
    // - modalità Preventivi (sub-tab Gestionale Pratiche): SOLO preventivi.
    // - modalità Modulistica classica: tutto TRANNE preventivi.
    if (restrictToPreventivi) {
      if (form.formType !== "preventivo-pratica") return false;
    } else if (form.formType === "preventivo-pratica") {
      return false;
    }

    // Text search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !form.clientName?.toLowerCase().includes(query) &&
        !form.formName?.toLowerCase().includes(query) &&
        !form.formData?.practiceType?.toLowerCase().includes(query)
      ) {
        return false;
      }
    }
    
    // Management type filter (based on client's managementType)
    if (filterManagementType !== "all") {
      const client = clients.find(c => c.id === form.clientId);
      const clientManagement = client?.managementType || "studio";
      if (clientManagement !== filterManagementType) return false;
    }

    // 22/07/2026 · v1.8.0 — Filtro "Solo miei" (attivo solo per preventivi).
    // Match su `operatorId` (preferito) con fallback su `userId` per
    // preventivi legacy dove operatorId poteva non essere denormalizzato.
    if (restrictToPreventivi && filterOnlyMine && currentOperator?.id) {
      const opId = currentOperator.id;
      const userId = currentOperator.userId || currentOperator.id;
      if (form.operatorId !== opId && form.userId !== userId) {
        return false;
      }
    }

    // 15/07/2026 — Quote status filter (attivo solo per preventivi).
    // Stati: bozza · inviato · accettato · rifiutato · convertito
    // "convertito" = presenza di `convertedPracticeId` (stato derivato).
    if (restrictToPreventivi && filterQuoteStatus !== "all") {
      if (filterQuoteStatus === "convertito") {
        if (!form.convertedPracticeId) return false;
      } else {
        // Escludo quelli già convertiti dagli altri status (sono in "convertito")
        if (form.convertedPracticeId) return false;
        const status = form.quoteStatus || "bozza";
        if (status !== filterQuoteStatus) return false;
      }
    }
    
    // Date range filter
    if (dateRange.from || dateRange.to) {
      const formDate = new Date(form.createdAt);
      if (isNaN(formDate.getTime())) return true; // Keep if no valid date
      
      if (dateRange.from && formDate < new Date(dateRange.from)) return false;
      if (dateRange.to) {
        const toDate = new Date(dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        if (formDate > toDate) return false;
      }
    }
    
    return true;
  });

  // Render form view
  if (activeView === "form" && selectedFormType) {
    return (
      <FormRenderer
        formType={selectedFormType}
        editingForm={editingForm}
        clients={clients}
        operators={operators}
        cases={cases}
        currentOperator={currentOperator}
        onBack={() => setActiveView("templates")}
        onSaved={handleFormSaved}
        onOpenClientDialog={onOpenClientDialog}
        configTables={configTables}
      />
    );
  }

  return (
    <div className="space-y-2" data-module="modulistica-module">
      {/* Header compact — v1.9.0 UX Operativa v2: icona + titolo + tab
          switcher su una sola riga; Card wrapper rimosso; descrizione
          spostata in tooltip. Recuperati ~55px verticali per far spazio
          alla galleria template. */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-1 py-1" data-testid="modulistica-module-header">
        <div className="flex items-center gap-3">
          <span
            className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "var(--gis-green-50)" }}
          >
            <FileSignature className="h-4 w-4" style={{ color: "var(--gis-green)" }} />
          </span>
          <div>
            <h2 className="gis-heading font-bold text-slate-900 text-lg leading-tight">
              {restrictToPreventivi ? "Preventivi" : "Modulistica"}
            </h2>
            <p className="text-[11px] text-slate-500 gis-body">
              {restrictToPreventivi
                ? "Crea, archivia, modifica e stampa preventivi · conversione in pratica"
                : "Moduli e documenti stampabili per lo studio"}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs: Templates / Archive */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="templates" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <PlusCircle className="h-4 w-4 mr-2" />
            {restrictToPreventivi ? "Modulo Preventivo" : "Galleria modelli"}
          </TabsTrigger>
          <TabsTrigger value="archive" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">
            <Archive className="h-4 w-4 mr-2" />
            {/* Counter ufficiale (28/02/2026): mostra il numero
                FILTRATO quando l'utente ha applicato filtri,
                altrimenti il totale archiviato. `filteredForms.length`
                soddisfa entrambi i casi perché senza filtri attivi
                coincide con `savedForms.length`. */}
            Archivio ({filteredForms.length})
          </TabsTrigger>
        </TabsList>

        {/* Templates View */}
        <TabsContent value="templates" className="mt-3 space-y-3">
          {/* Controls: Category Filter + View Toggle */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px]" data-testid="filter-template-category">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">
                {filteredTemplates.length} moduli
              </span>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <Button
                variant={templateViewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setTemplateViewMode("list")}
                className="h-8 px-3"
                data-testid="view-mode-list"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={templateViewMode === "cards" ? "default" : "ghost"}
                size="sm"
                onClick={() => setTemplateViewMode("cards")}
                className="h-8 px-3"
                data-testid="view-mode-cards"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Compact List View */}
          <div className={templateViewMode === "list" ? "" : "hidden"}>
            <Card className="shadow-md overflow-hidden">
              <div className="divide-y divide-slate-100">
                {filteredTemplates.map((form) => {
                  const IconComponent = form.icon;
                  return (
                    <div
                      key={form.id}
                      className="flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors group"
                      onClick={() => handleOpenForm(form.id)}
                      data-testid={`form-row-${form.id}`}
                    >
                      <div className="h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-5 w-5 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">
                            {form.name}
                          </h3>
                          <Badge variant="outline" className="text-xs bg-slate-50">
                            {form.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 truncate">
                          {form.description}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Cards View */}
          <div className={templateViewMode === "cards" ? "grid gap-4 md:grid-cols-2 lg:grid-cols-3" : "hidden"}>
            {filteredTemplates.map((form) => {
              const IconComponent = form.icon;
              return (
                <Card
                  key={form.id}
                  className="cursor-pointer hover:shadow-lg hover:border-teal-300 transition-all border-l-4 border-l-teal-500"
                  onClick={() => handleOpenForm(form.id)}
                  data-testid={`form-card-${form.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-6 w-6 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base text-slate-900">
                          {form.name}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                          {form.description}
                        </p>
                        <Badge variant="secondary" className="mt-2 text-xs">
                          {form.category}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Archive View */}
        <TabsContent value="archive" className="mt-6 space-y-4">
          {/* 15/07/2026 — Chip contatori stato preventivo (solo Preventivi).
              Cliccabili per filtrare l'archivio. Conteggio calcolato sul
              totale `savedForms` (non sui filtered) così l'utente vede
              sempre la fotografia completa dello stato d'avanzamento. */}
          {restrictToPreventivi && (() => {
            const preventivi = savedForms.filter(f => f.formType === "preventivo-pratica");
            const total = preventivi.length;
            const converted = preventivi.filter(f => f.convertedPracticeId).length;
            const notConverted = preventivi.filter(f => !f.convertedPracticeId);
            const bozza = notConverted.filter(f => (f.quoteStatus || "bozza") === "bozza").length;
            const inviato = notConverted.filter(f => f.quoteStatus === "inviato").length;
            const accettato = notConverted.filter(f => f.quoteStatus === "accettato").length;
            const rifiutato = notConverted.filter(f => f.quoteStatus === "rifiutato").length;
            const chips = [
              { key: "all",        label: "Tutti",      count: total,      activeCls: "bg-slate-800 text-white",   idleCls: "bg-white text-slate-700 border-slate-300 hover:bg-slate-50" },
              { key: "bozza",      label: "Bozza",      count: bozza,      activeCls: "bg-slate-600 text-white",   idleCls: "bg-white text-slate-700 border-slate-300 hover:bg-slate-50" },
              { key: "inviato",    label: "Inviati",    count: inviato,    activeCls: "bg-blue-600 text-white",    idleCls: "bg-white text-blue-700 border-blue-300 hover:bg-blue-50" },
              { key: "accettato",  label: "Accettati",  count: accettato,  activeCls: "bg-green-600 text-white",   idleCls: "bg-white text-green-700 border-green-300 hover:bg-green-50" },
              { key: "rifiutato",  label: "Rifiutati",  count: rifiutato,  activeCls: "bg-red-600 text-white",     idleCls: "bg-white text-red-700 border-red-300 hover:bg-red-50" },
              { key: "convertito", label: "Convertiti", count: converted,  activeCls: "bg-emerald-600 text-white", idleCls: "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50" },
            ];
            return (
              <div className="flex flex-wrap items-center gap-2" data-testid="preventivi-status-chips">
                {chips.map((chip) => {
                  const isActive = filterQuoteStatus === chip.key;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setFilterQuoteStatus(chip.key)}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${isActive ? chip.activeCls + " border-transparent" : chip.idleCls}`}
                      data-testid={`preventivi-status-chip-${chip.key}`}
                    >
                      <span>{chip.label}</span>
                      <span className={`inline-flex items-center justify-center rounded-full px-1.5 min-w-[22px] text-[11px] font-semibold ${isActive ? "bg-white/25" : "bg-slate-100"}`}>
                        {chip.count}
                      </span>
                    </button>
                  );
                })}
                {/* v1.8.0 — Toggle "Solo miei" preventivi. Filtra sui preventivi
                    creati dall'operatore corrente (match operatorId/userId). */}
                {currentOperator?.id && (() => {
                  const myCount = savedForms.filter(f =>
                    f.formType === "preventivo-pratica" &&
                    (f.operatorId === currentOperator.id ||
                     f.userId === (currentOperator.userId || currentOperator.id))
                  ).length;
                  return (
                    <>
                      <div className="h-6 w-px bg-slate-300 mx-0.5" />
                      <button
                        type="button"
                        onClick={() => setFilterOnlyMine((v) => !v)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          filterOnlyMine
                            ? "bg-[var(--gis-green)] text-white border-transparent"
                            : "bg-white text-[var(--gis-green-dark)] border-[var(--gis-green-200)] hover:bg-[var(--gis-green-50)]"
                        }`}
                        title={
                          filterOnlyMine
                            ? "Mostra i preventivi di tutti gli operatori"
                            : `Mostra solo i preventivi creati da te (${currentOperator.fullName || currentOperator.name || "operatore"})`
                        }
                        data-testid="preventivi-only-mine-toggle"
                      >
                        <User className="h-3.5 w-3.5" />
                        <span>Solo miei</span>
                        <span className={`inline-flex items-center justify-center rounded-full px-1.5 min-w-[22px] text-[11px] font-semibold ${filterOnlyMine ? "bg-white/25" : "bg-[var(--gis-green-50)] text-[var(--gis-green-dark)]"}`}>
                          {myCount}
                        </span>
                      </button>
                    </>
                  );
                })()}
              </div>
            );
          })()}

          {/* Filters — 15/07/2026: unificato in una singola riga
              orizzontale su schermi larghi (flex-wrap fallback su
              viewport piccole). Rimosso il grid multi-riga precedente. */}
          <Card className="shadow-md">
            <CardContent className="p-4 space-y-2">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Cerca modulo..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-forms-input"
                  />
                </div>
                {!restrictToPreventivi && (
                  <Select value={filterFormType} onValueChange={setFilterFormType}>
                    <SelectTrigger data-testid="filter-form-type" className="w-[170px]">
                      <SelectValue placeholder="Tipo modulo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i tipi</SelectItem>
                      {sortedTemplates.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={filterManagementType} onValueChange={setFilterManagementType}>
                  <SelectTrigger data-testid="filter-form-management" className="w-[180px]">
                    <SelectValue placeholder="Gestione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le gestioni</SelectItem>
                    <SelectItem value="studio">Gestione Studio</SelectItem>
                    <SelectItem value="agenzia">Gestione Agenzia</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterClient} onValueChange={setFilterClient}>
                  <SelectTrigger data-testid="filter-form-client" className="w-[180px]">
                    <SelectValue placeholder="Cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i clienti</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.fullName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Label htmlFor="mod-date-from" className="text-xs text-slate-500 whitespace-nowrap">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Dal
                  </Label>
                  <Input
                    id="mod-date-from"
                    type="date"
                    value={dateRange.from || ""}
                    onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value || null }))}
                    className="h-9 w-[150px]"
                    data-testid="date-from"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Label htmlFor="mod-date-to" className="text-xs text-slate-500 whitespace-nowrap">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Al
                  </Label>
                  <Input
                    id="mod-date-to"
                    type="date"
                    value={dateRange.to || ""}
                    onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value || null }))}
                    className="h-9 w-[150px]"
                    data-testid="date-to"
                  />
                </div>
              </div>

              {/* Results count and reset */}
              <div className="flex items-center justify-between text-sm pt-2">
                <div className="text-slate-500">
                  {filteredForms.length === savedForms.length ? (
                    <span>{savedForms.length} moduli</span>
                  ) : (
                    <span>{filteredForms.length} di {savedForms.length} moduli</span>
                  )}
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="h-7 text-xs text-slate-500 hover:text-slate-700"
                    data-testid="reset-filters"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Azzera filtri
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Forms List */}
          {/* Loading State */}
          <Card className={`shadow-md ${isLoadingForms ? '' : 'hidden'}`}>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
              <span className="ml-3 text-slate-600">Caricamento moduli...</span>
            </CardContent>
          </Card>
          
          {/* Empty State */}
          <Card className={`shadow-md ${!isLoadingForms && filteredForms.length === 0 ? '' : 'hidden'}`}>
            <CardContent className="text-center py-12">
              <Archive className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {hasActiveFilters ? "Nessun modulo trovato con questi filtri" : "Nessun modulo salvato"}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {hasActiveFilters ? "Prova a modificare i criteri di ricerca" : "I moduli compilati e salvati appariranno qui"}
              </p>
              {!hasActiveFilters && (
                <Button
                  className="mt-4 bg-teal-600 hover:bg-teal-700"
                  onClick={() => setActiveView("templates")}
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Apri galleria modelli
                </Button>
              )}
            </CardContent>
          </Card>
          
          {/* Forms List */}
          <div className={`space-y-3 ${!isLoadingForms && filteredForms.length > 0 ? '' : 'hidden'}`}>
            {filteredForms.map((form) => {
              const formTemplate = availableForms.find(f => f.id === form.formType);
              const IconComponent = formTemplate?.icon || FileText;

              // Title custom per i preventivi-pratica (28/02/2026):
              // formato ufficio: "Preventivo n. {N} — {cliente} — {attività} — {data}"
              // - quoteNumber: top-level (nuovi) o formData.quoteNumber (storici)
              // - attività: formData.lineItems[0].description, fallback
              //   "Attività non indicata" per documenti storici senza righe
              // - data: createdAt formattata it-IT (gg/mm/aaaa)
              let isQuote = form.formType === "preventivo-pratica";
              let quoteTitle = null;
              let revisionLabel = null;
              if (isQuote) {
                const fd = form.formData || {};
                const qNum = form.quoteNumber || fd.quoteNumber || "—";
                const cliName = form.clientName || fd.clientName || "Cliente non indicato";
                const firstActivity = Array.isArray(fd.lineItems)
                  ? (fd.lineItems.find((li) => (li?.description || "").trim()) || {}).description
                  : "";
                const activityLabel = (firstActivity || "").trim() || "Attività non indicata";
                let dateLabel = "";
                try {
                  dateLabel = form.createdAt
                    ? new Date(form.createdAt).toLocaleDateString("it-IT")
                    : "";
                } catch { dateLabel = ""; }
                const segments = [
                  `Preventivo n. ${qNum}`,
                ];
                // Etichetta revisione professionale (14/07/2026):
                // "rev. 03 del 14/07/2026" apparirà a fianco del numero.
                const rev = Number(form.revision ?? fd.revision ?? 0);
                if (rev > 0) {
                  let revDate = "";
                  try {
                    const rd = form.revisionDate || fd.revisionDate || form.updatedAt;
                    revDate = rd ? new Date(rd).toLocaleDateString("it-IT") : "";
                  } catch { revDate = ""; }
                  const rr = String(rev).padStart(2, "0");
                  revisionLabel = revDate ? `rev. ${rr} del ${revDate}` : `rev. ${rr}`;
                  segments[0] += ` (${revisionLabel})`;
                }
                segments.push(cliName, activityLabel);
                if (dateLabel) segments.push(dateLabel);
                quoteTitle = segments.join(" — ");
              }

              return (
                <Card key={form.id} className="shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-slate-900" data-testid={`form-row-title-${form.id}`}>
                              {quoteTitle || form.formName || formTemplate?.name || "Modulo"}
                            </h4>
                            <Badge variant="secondary" className="text-xs">
                              {formTemplate?.name || form.formType}
                            </Badge>
                            {isQuote && (form.formData?.internalNotes || "").trim() && (
                              <span
                                title="Ci sono note interne compilate su questo preventivo"
                                className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                                data-testid={`form-row-has-notes-${form.id}`}
                              >
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                                Note
                              </span>
                            )}
                            {isQuote && form.quoteStatus && (
                              <Badge
                                variant="outline"
                                className="text-xs capitalize"
                                data-testid={`form-row-status-${form.id}`}
                              >
                                {form.quoteStatus}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span className={(!isQuote && form.clientName) ? "flex items-center gap-1" : "hidden"}>
                              {form.clientName ? (
                                <>
                                  <User className="h-3 w-3" />
                                  {form.clientName}
                                </>
                              ) : null}
                            </span>
                            <span className={isQuote ? "hidden" : "flex items-center gap-1"}>
                              <Calendar className="h-3 w-3" />
                              {new Date(form.createdAt).toLocaleDateString("it-IT")}
                            </span>
                            <span className={form.operatorName ? "flex items-center gap-1" : "hidden"}>
                              {form.operatorName ? (
                                <>
                                  <Building2 className="h-3 w-3" />
                                  {form.operatorName}
                                </>
                              ) : null}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditForm(form)}
                            data-testid={`view-form-${form.id}`}
                            title="Anteprima / apri modulo"
                            aria-label="Anteprima modulo"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => handleEditForm(form)}
                            data-testid={`edit-form-${form.id}`}
                            title="Modifica modulo"
                            aria-label="Modifica modulo"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-teal-600 border-teal-200 hover:bg-teal-50"
                            onClick={() => handleDownloadPDF(form.id)}
                            data-testid={`download-form-${form.id}`}
                            title="Scarica PDF"
                            aria-label="Scarica PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDeleteForm(form.id)}
                            data-testid={`delete-form-${form.id}`}
                            title="Elimina modulo"
                            aria-label="Elimina modulo"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Feature flag: Templates enabled for dynamic rendering
// Migration order: consenso-privacy (done) → delega (done) → incarico-professionale (done) → presa-in-carico (reverted to hardcoded)
const DYNAMIC_ENABLED_TEMPLATES = ["consenso-privacy", "delega", "incarico-professionale"];

// Form Renderer Component with Feature Flag Support
function FormRenderer({ formType, editingForm, clients, operators, cases, currentOperator, onBack, onSaved, onOpenClientDialog, configTables }) {
  const [useDynamic, setUseDynamic] = useState(false);
  const [template, setTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Feature flag: Check if dynamic template is available for this form type
  useEffect(() => {
    async function checkDynamicAvailability() {
      // Only enable dynamic rendering for templates in the enabled list
      if (DYNAMIC_ENABLED_TEMPLATES.includes(formType)) {
        try {
          setLoadingTemplate(true);
          const response = await api.checkDynamicTemplateAvailable(formType);
          
          if (response.dynamicAvailable) {
            // Load the template
            const templateData = await api.getTemplate(formType);
            setTemplate(templateData);
            setUseDynamic(true);
          }
        } catch (error) {
          console.warn("Dynamic template not available, using fallback:", error);
          setUseDynamic(false);
        } finally {
          setLoadingTemplate(false);
        }
      }
    }
    
    checkDynamicAvailability();
  }, [formType]);

  // Show loading state while checking template availability
  if (loadingTemplate) {
    return (
      <Card className="shadow-md">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          <span className="ml-3 text-slate-600">Caricamento modulo...</span>
        </CardContent>
      </Card>
    );
  }

  // Dynamic rendering for enabled templates
  if (useDynamic && template && DYNAMIC_ENABLED_TEMPLATES.includes(formType)) {
    return (
      <DynamicFormRenderer
        template={template}
        editingForm={editingForm}
        clients={clients}
        operators={operators}
        cases={cases}
        currentOperator={currentOperator}
        onBack={onBack}
        onSaved={onSaved}
      />
    );
  }

  // Fallback to hardcoded forms
  if (formType === "presa-in-carico") {
    return (
      <MandatoPraticaForm
        editingForm={editingForm}
        clients={clients}
        operators={operators}
        cases={cases}
        currentOperator={currentOperator}
        onBack={onBack}
        onSaved={onSaved}
        onOpenClientDialog={onOpenClientDialog}
        configTables={configTables}
      />
    );
  }
  
  if (formType === "delega") {
    return (
      <DelegaForm
        editingForm={editingForm}
        clients={clients}
        operators={operators}
        cases={cases}
        currentOperator={currentOperator}
        onBack={onBack}
        onSaved={onSaved}
      />
    );
  }
  
  if (formType === "incarico-professionale") {
    return (
      <IncaricoProfessionaleForm
        editingForm={editingForm}
        clients={clients}
        operators={operators}
        cases={cases}
        currentOperator={currentOperator}
        onBack={onBack}
        onSaved={onSaved}
      />
    );
  }
  
  if (formType === "consenso-privacy") {
    // Fallback to hardcoded ConsensoPrivacyForm if dynamic not available
    return (
      <ConsensoPrivacyForm
        editingForm={editingForm}
        clients={clients}
        operators={operators}
        cases={cases}
        currentOperator={currentOperator}
        onBack={onBack}
        onSaved={onSaved}
      />
    );
  }

  if (formType === "preventivo-pratica") {
    return (
      <SafeBoundary onBack={onBack}>
        <PreventivoForm
          editingForm={editingForm}
          clients={clients}
          operators={operators}
          cases={cases}
          currentOperator={currentOperator}
          onBack={onBack}
          onSaved={onSaved}
          onOpenClientDialog={onOpenClientDialog}
        />
      </SafeBoundary>
    );
  }
  
  // Fallback for unknown form types
  return (
    <Card className="shadow-md">
      <CardContent className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
        <p className="text-slate-600">Tipo di modulo non supportato: {formType}</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai Moduli
        </Button>
      </CardContent>
    </Card>
  );
}

// Mandato Pratica Form Component (formerly Presa in Carico)
function PresaInCaricoForm({ editingForm, clients, operators, cases, currentOperator, onBack, onSaved, onOpenClientDialog }) {
  const [formData, setFormData] = useState({
    clientName: "",
    clientId: "",
    taxCode: "",
    vatNumber: "",
    clientAddress: "",
    clientEmail: "",
    clientPhone: "",
    clientSDI: "",
    // Persona fisica in qualità di (optional representative)
    repName: "",
    repTaxCode: "",
    repPhone: "",
    repEmail: "",
    // Practice data
    practiceType: "",
    practiceDescription: "",
    intakeDate: new Date().toISOString().split("T")[0],
    operator: currentOperator?.fullName || "",
    operatorId: currentOperator?.id || "",
    notes: "",
    linkedCaseId: "",
    // Privacy consent
    privacyConsent: ""
  });
  const [brandingContext, setBrandingContext] = useState("studio");
  const [brandingLocked, setBrandingLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showRepresentative, setShowRepresentative] = useState(false);

  // Load editing form data
  useEffect(() => {
    if (editingForm) {
      const data = editingForm.formData || {};
      setFormData({
        clientName: data.clientName || editingForm.clientName || "",
        clientId: editingForm.clientId || "",
        taxCode: data.taxCode || "",
        vatNumber: data.vatNumber || "",
        clientAddress: data.clientAddress || "",
        clientEmail: data.clientEmail || "",
        clientPhone: data.clientPhone || "",
        clientSDI: data.clientSDI || "",
        repName: data.repName || "",
        repTaxCode: data.repTaxCode || "",
        repPhone: data.repPhone || "",
        repEmail: data.repEmail || "",
        practiceType: data.practiceType || "",
        practiceDescription: data.practiceDescription || "",
        intakeDate: data.intakeDate || new Date().toISOString().split("T")[0],
        operator: data.operator || editingForm.operatorName || "",
        operatorId: editingForm.operatorId || "",
        notes: data.notes || "",
        linkedCaseId: editingForm.caseId || "",
        privacyConsent: data.privacyConsent || ""
      });
      setBrandingContext(editingForm.brandingContext || "studio");
      if (editingForm.caseId) {
        setBrandingLocked(true);
      }
      // Show representative section if data exists
      if (data.repName || data.repTaxCode || data.repPhone || data.repEmail) {
        setShowRepresentative(true);
      }
    }
  }, [editingForm]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.clientName.trim()) newErrors.clientName = "Campo obbligatorio";
    if (!formData.taxCode.trim()) newErrors.taxCode = "Campo obbligatorio";
    if (!formData.practiceType.trim()) newErrors.practiceType = "Campo obbligatorio";
    if (!formData.practiceDescription.trim()) newErrors.practiceDescription = "Campo obbligatorio";
    if (!formData.intakeDate) newErrors.intakeDate = "Campo obbligatorio";
    if (!formData.operator.trim()) newErrors.operator = "Campo obbligatorio";
    if (!formData.privacyConsent) newErrors.privacyConsent = "Il consenso privacy è obbligatorio";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    setSaveSuccess(false);
  };

  const handleCaseSelect = (caseId) => {
    if (!caseId) {
      setFormData(prev => ({ ...prev, linkedCaseId: "" }));
      setBrandingLocked(false);
      return;
    }
    
    const selectedCase = cases.find(c => c.id === caseId);
    if (selectedCase) {
      const inferredBranding = inferBrandingFromPracticeType(selectedCase.practiceType);
      setFormData(prev => ({
        ...prev,
        linkedCaseId: caseId,
        clientId: selectedCase.clientId || prev.clientId,
        practiceType: selectedCase.practiceType || prev.practiceType,
        practiceDescription: selectedCase.subject || prev.practiceDescription
      }));
      const linkedClient = clients.find(c => c.id === selectedCase.clientId);
      if (linkedClient) {
        setFormData(prev => ({
          ...prev,
          clientName: linkedClient.fullName,
          taxCode: linkedClient.taxCode || "",
          vatNumber: linkedClient.vatNumber || "",
          clientAddress: linkedClient.address || "",
          clientEmail: linkedClient.email || "",
          clientPhone: linkedClient.phone || "",
          clientSDI: linkedClient.sdiCode || linkedClient.pec || ""
        }));
      }
      setBrandingContext(inferredBranding);
      setBrandingLocked(true);
    }
    setSaveSuccess(false);
  };

  const handleClientSelect = (clientName) => {
    const client = clients.find(c => c.fullName === clientName);
    if (client) {
      setFormData(prev => ({
        ...prev,
        clientName: client.fullName,
        clientId: client.id,
        taxCode: client.taxCode || "",
        vatNumber: client.vatNumber || "",
        clientAddress: client.address || "",
        clientEmail: client.email || "",
        clientPhone: client.phone || "",
        clientSDI: client.sdiCode || client.pec || ""
      }));
    } else {
      updateField("clientName", clientName);
      updateField("clientId", "");
    }
    setSaveSuccess(false);
  };

  const handleOperatorSelect = (operatorName) => {
    const operator = operators.find(o => o.fullName === operatorName);
    if (operator) {
      setFormData(prev => ({
        ...prev,
        operator: operator.fullName,
        operatorId: operator.id
      }));
    } else {
      updateField("operator", operatorName);
      updateField("operatorId", "");
    }
    setSaveSuccess(false);
  };

  const handleSave = async (andPrint = false) => {
    if (!validateForm()) return;
    
    try {
      setIsSaving(true);
      
      const linkedCase = formData.linkedCaseId ? cases.find(c => c.id === formData.linkedCaseId) : null;
      
      // Generate protocol number based on branding
      const protocolPrefix = brandingContext === "agenzia" ? "GA" : "GS";
      
      const saveData = {
        formType: "presa-in-carico",
        formName: `Mandato Pratica - ${formData.clientName}`,
        clientId: formData.clientId || null,
        clientName: formData.clientName,
        caseId: formData.linkedCaseId || null,
        caseNumber: linkedCase?.caseNumber || null,
        operatorId: formData.operatorId || null,
        operatorName: formData.operator,
        brandingContext: brandingContext,
        protocolPrefix: protocolPrefix,
        formData: {
          clientName: formData.clientName,
          taxCode: formData.taxCode,
          vatNumber: formData.vatNumber,
          clientAddress: formData.clientAddress,
          clientEmail: formData.clientEmail,
          clientPhone: formData.clientPhone,
          clientSDI: formData.clientSDI,
          repName: formData.repName,
          repTaxCode: formData.repTaxCode,
          repPhone: formData.repPhone,
          repEmail: formData.repEmail,
          practiceType: formData.practiceType,
          practiceDescription: formData.practiceDescription,
          intakeDate: formData.intakeDate,
          operator: formData.operator,
          notes: formData.notes,
          linkedCaseId: formData.linkedCaseId || null,
          privacyConsent: formData.privacyConsent
        }
      };
      
      let savedForm;
      if (editingForm) {
        savedForm = await api.updateForm(editingForm.id, saveData);
      } else {
        savedForm = await api.createForm(saveData);
      }
      
      setSaveSuccess(true);
      
      if (andPrint) {
        await api.downloadFormPDF(savedForm.id);
      }
      
      setTimeout(() => {
        onSaved();
      }, 1000);
      
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} data-testid="back-to-forms-button">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai Moduli
        </Button>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {editingForm ? "Modifica Mandato" : "Mandato Pratica"}
          </h2>
          <p className="text-sm text-slate-500">
            {editingForm ? "Modifica e salva il mandato" : "Compila, salva e stampa il mandato"}
          </p>
        </div>
      </div>

      {/* Form Card */}
      <Card className="shadow-md">
        <CardHeader className="border-b">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Mandato Pratica</CardTitle>
              <CardDescription>Gestionale Gruppo Impresa</CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Client Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Dati Cliente
                </h3>
                {onOpenClientDialog && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onOpenClientDialog}
                    data-testid="create-client-from-mandato"
                  >
                    <PlusCircle className="h-4 w-4 mr-1" />
                    Nuovo Cliente
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="clientName">Nome / Ragione Sociale *</Label>
                  <Input
                    id="clientName"
                    data-testid="modulistica-client-name"
                    value={formData.clientName}
                    onChange={(e) => handleClientSelect(e.target.value)}
                    placeholder="Es. Mario Rossi / Studio Alfa Srl"
                    className={errors.clientName ? "border-red-500" : ""}
                    list="client-suggestions"
                  />
                  <datalist id="client-suggestions">
                    {clients.map(c => (
                      <option key={c.id} value={c.fullName} />
                    ))}
                  </datalist>
                  <FieldError error={errors.clientName} />
                </div>
                <div>
                  <Label htmlFor="taxCode">Codice Fiscale *</Label>
                  <Input
                    id="taxCode"
                    data-testid="modulistica-tax-code"
                    value={formData.taxCode}
                    onChange={(e) => updateField("taxCode", e.target.value)}
                    placeholder="Es. RSSMRA80A01H501X"
                    className={errors.taxCode ? "border-red-500" : ""}
                  />
                  <FieldError error={errors.taxCode} />
                </div>
                <div>
                  <Label htmlFor="vatNumber">Partita IVA</Label>
                  <Input
                    id="vatNumber"
                    data-testid="modulistica-vat-number"
                    value={formData.vatNumber}
                    onChange={(e) => updateField("vatNumber", e.target.value)}
                    placeholder="Es. 01234567890"
                  />
                </div>
                <div>
                  <Label htmlFor="clientAddress">Residenza / Sede</Label>
                  <Input
                    id="clientAddress"
                    value={formData.clientAddress}
                    onChange={(e) => updateField("clientAddress", e.target.value)}
                    placeholder="Via, CAP, Città"
                  />
                </div>
                <div>
                  <Label htmlFor="clientEmail">Email</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => updateField("clientEmail", e.target.value)}
                    placeholder="email@esempio.it"
                  />
                </div>
                <div>
                  <Label htmlFor="clientPhone">Cellulare</Label>
                  <Input
                    id="clientPhone"
                    value={formData.clientPhone}
                    onChange={(e) => updateField("clientPhone", e.target.value)}
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="clientSDI">Codice SDI / PEC</Label>
                  <Input
                    id="clientSDI"
                    value={formData.clientSDI}
                    onChange={(e) => updateField("clientSDI", e.target.value)}
                    placeholder="Codice SDI o PEC per fatturazione elettronica"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Representative Section (optional) */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Persona fisica in qualità di
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRepresentative(!showRepresentative)}
                >
                  {showRepresentative ? "Nascondi" : "Mostra sezione"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Compilare solo se il cliente agisce tramite un rappresentante
              </p>
              {showRepresentative && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label htmlFor="repName">Cognome e Nome</Label>
                    <Input
                      id="repName"
                      value={formData.repName}
                      onChange={(e) => updateField("repName", e.target.value)}
                      placeholder="Es. Rossi Mario"
                    />
                  </div>
                  <div>
                    <Label htmlFor="repTaxCode">Codice Fiscale</Label>
                    <Input
                      id="repTaxCode"
                      value={formData.repTaxCode}
                      onChange={(e) => updateField("repTaxCode", e.target.value)}
                      placeholder="Es. RSSMRA80A01H501X"
                    />
                  </div>
                  <div>
                    <Label htmlFor="repPhone">Telefono</Label>
                    <Input
                      id="repPhone"
                      value={formData.repPhone}
                      onChange={(e) => updateField("repPhone", e.target.value)}
                      placeholder="+39 333 1234567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="repEmail">Email</Label>
                    <Input
                      id="repEmail"
                      type="email"
                      value={formData.repEmail}
                      onChange={(e) => updateField("repEmail", e.target.value)}
                      placeholder="email@esempio.it"
                    />
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Practice Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Dati Pratica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="practiceType">Tipo Pratica *</Label>
                  <Input
                    id="practiceType"
                    data-testid="modulistica-practice-type"
                    value={formData.practiceType}
                    onChange={(e) => updateField("practiceType", e.target.value)}
                    placeholder="Es. Assunzione, Licenziamento, Consulenza..."
                    className={errors.practiceType ? "border-red-500" : ""}
                  />
                  <FieldError error={errors.practiceType} />
                </div>
                <div>
                  <Label htmlFor="intakeDate">Data Mandato *</Label>
                  <Input
                    id="intakeDate"
                    type="date"
                    data-testid="modulistica-intake-date"
                    value={formData.intakeDate}
                    onChange={(e) => updateField("intakeDate", e.target.value)}
                    className={errors.intakeDate ? "border-red-500" : ""}
                  />
                  <FieldError error={errors.intakeDate} />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="practiceDescription">Descrizione Pratica *</Label>
                  <Textarea
                    id="practiceDescription"
                    data-testid="modulistica-practice-description"
                    value={formData.practiceDescription}
                    onChange={(e) => updateField("practiceDescription", e.target.value)}
                    placeholder="Descrizione dettagliata della pratica..."
                    rows={3}
                    className={errors.practiceDescription ? "border-red-500" : ""}
                  />
                  <FieldError error={errors.practiceDescription} />
                </div>
              </div>
            </div>

            <Separator />

            {/* Operator Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Operatore
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="operator">Nominativo *</Label>
                  <Input
                    id="operator"
                    data-testid="modulistica-operator"
                    value={formData.operator}
                    onChange={(e) => handleOperatorSelect(e.target.value)}
                    placeholder="Seleziona operatore"
                    className={errors.operator ? "border-red-500" : ""}
                    list="operator-suggestions"
                  />
                  <datalist id="operator-suggestions">
                    {operators.filter(op => op.status !== "sospeso").map(o => (
                      <option key={o.id} value={o.fullName} />
                    ))}
                  </datalist>
                  <FieldError error={errors.operator} />
                  <p className="text-xs text-slate-400 mt-1">Non verrà stampato nel documento</p>
                </div>
                <div>
                  <Label htmlFor="notes">Note Aggiuntive</Label>
                  <Input
                    id="notes"
                    data-testid="modulistica-notes"
                    value={formData.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                    placeholder="Eventuali note..."
                  />
                </div>
                {/* Link to existing practice */}
                <div className={cases && cases.length > 0 ? "md:col-span-2" : "hidden"}>
                  {cases && cases.length > 0 ? (
                    <div>
                      <Label>Collega a Pratica Esistente (opzionale)</Label>
                      <Select value={formData.linkedCaseId || "none"} onValueChange={(v) => handleCaseSelect(v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="link-case-select">
                          <SelectValue placeholder="Seleziona pratica..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Nessuna pratica collegata —</SelectItem>
                          {cases.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.caseNumber} - {c.subject?.substring(0, 40) || 'N/D'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InfoMessage show={formData.linkedCaseId}>
                        <p className="text-xs text-teal-600 mt-1">
                          Il branding e protocollo verranno impostati in base al tipo pratica
                        </p>
                      </InfoMessage>
                    </div>
                  ) : null}
                </div>
                <div className="md:col-span-2">
                  <Label>Intestazione Documento (Branding)</Label>
                  <InfoMessage show={brandingLocked} className="mb-2">
                    <p className="text-xs text-amber-600">
                      Branding determinato dal tipo pratica collegata
                    </p>
                  </InfoMessage>
                  <div className="flex gap-4 mt-2">
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'studio' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="branding"
                        value="studio"
                        checked={brandingContext === 'studio'}
                        onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)}
                        disabled={brandingLocked}
                        className="sr-only"
                      />
                      <Building2 className={`h-5 w-5 ${brandingContext === 'studio' ? 'text-teal-600' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-medium ${brandingContext === 'studio' ? 'text-teal-700' : 'text-slate-600'}`}>Gestione Studio (GS-)</p>
                        <p className="text-xs text-slate-500">Logo e protocollo Studio</p>
                      </div>
                    </label>
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'agenzia' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="branding"
                        value="agenzia"
                        checked={brandingContext === 'agenzia'}
                        onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)}
                        disabled={brandingLocked}
                        className="sr-only"
                      />
                      <Briefcase className={`h-5 w-5 ${brandingContext === 'agenzia' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-medium ${brandingContext === 'agenzia' ? 'text-orange-700' : 'text-slate-600'}`}>Gestione Agenzia (GA-)</p>
                        <p className="text-xs text-slate-500">Logo Prisma Servizi</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Privacy Consent Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Consenso al trattamento dei dati
              </h3>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 mb-4">
                  Autorizzo il trattamento dei miei dati personali (inclusi email e cellulare) ai sensi del Reg. UE 2016/679 (GDPR) per le finalità connesse alla gestione della pratica.
                </p>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.privacyConsent === 'SI' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input
                      type="radio"
                      name="privacyConsent"
                      value="SI"
                      checked={formData.privacyConsent === 'SI'}
                      onChange={(e) => updateField("privacyConsent", e.target.value)}
                      className="sr-only"
                    />
                    <CheckCircle2 className={`h-5 w-5 ${formData.privacyConsent === 'SI' ? 'text-green-600' : 'text-slate-400'}`} />
                    <span className={`font-medium ${formData.privacyConsent === 'SI' ? 'text-green-700' : 'text-slate-600'}`}>SI, autorizzo</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.privacyConsent === 'NO' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input
                      type="radio"
                      name="privacyConsent"
                      value="NO"
                      checked={formData.privacyConsent === 'NO'}
                      onChange={(e) => updateField("privacyConsent", e.target.value)}
                      className="sr-only"
                    />
                    <X className={`h-5 w-5 ${formData.privacyConsent === 'NO' ? 'text-red-600' : 'text-slate-400'}`} />
                    <span className={`font-medium ${formData.privacyConsent === 'NO' ? 'text-red-700' : 'text-slate-600'}`}>NO, non autorizzo</span>
                  </label>
                </div>
                <FieldError error={errors.privacyConsent} />
              </div>
            </div>

            <Separator />

            {/* Signature Section Preview */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Area Firme (nel PDF)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-sm text-slate-600 mb-2">Firma del Cliente</p>
                  <div className="border-b-2 border-slate-300 h-12"></div>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-2">Firma per accettazione</p>
                  <div className="border-b-2 border-slate-300 h-12"></div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <div className="flex items-center gap-2 mr-auto min-h-[28px]">
          <div className={`flex items-center gap-2 text-green-600 ${saveSuccess ? '' : 'hidden'}`}>
            <CheckCircle2 className="h-5 w-5" />
            <span>Mandato salvato con successo!</span>
          </div>
        </div>
        <Button variant="outline" onClick={onBack}>
          Annulla
        </Button>
        <Button
          variant="outline"
          onClick={() => handleSave(false)}
          disabled={isSaving}
          data-testid="save-form-button"
        >
          <Save className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva
        </Button>
        <Button
          onClick={() => handleSave(true)}
          disabled={isSaving}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="save-and-print-button"
        >
          <Printer className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva e Scarica PDF
        </Button>
      </div>
    </div>
  );
}

// ==================== DELEGA FORM ====================
function DelegaForm({ editingForm, clients, operators, cases, currentOperator, onBack, onSaved }) {
  const [formData, setFormData] = useState({
    // Delegante (chi delega)
    deleganteName: "",
    deleganteId: "",
    deleganteTaxCode: "",
    deleganteAddress: "",
    deleganteBirthDate: "",
    deleganteBirthPlace: "",
    deleganteDocument: "",
    deleganteDocumentNumber: "",
    // Delegato (chi riceve la delega)
    delegatoName: "",
    delegatoTaxCode: "",
    delegatoRelation: "",
    // Oggetto delega
    delegaScope: "",
    delegaDescription: "",
    delegaValidFrom: new Date().toISOString().split("T")[0],
    delegaValidTo: "",
    // Operatore
    operator: currentOperator?.fullName || "",
    operatorId: currentOperator?.id || "",
    notes: "",
    // Practice link
    linkedCaseId: ""
  });
  const [brandingContext, setBrandingContext] = useState("studio");
  const [brandingLocked, setBrandingLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (editingForm) {
      const data = editingForm.formData || {};
      setFormData({
        deleganteName: data.deleganteName || editingForm.clientName || "",
        deleganteId: editingForm.clientId || "",
        deleganteTaxCode: data.deleganteTaxCode || "",
        deleganteAddress: data.deleganteAddress || "",
        deleganteBirthDate: data.deleganteBirthDate || "",
        deleganteBirthPlace: data.deleganteBirthPlace || "",
        deleganteDocument: data.deleganteDocument || "Carta d'Identità",
        deleganteDocumentNumber: data.deleganteDocumentNumber || "",
        delegatoName: data.delegatoName || "",
        delegatoTaxCode: data.delegatoTaxCode || "",
        delegatoRelation: data.delegatoRelation || "",
        delegaScope: data.delegaScope || "",
        delegaDescription: data.delegaDescription || "",
        delegaValidFrom: data.delegaValidFrom || new Date().toISOString().split("T")[0],
        delegaValidTo: data.delegaValidTo || "",
        operator: data.operator || editingForm.operatorName || "",
        operatorId: editingForm.operatorId || "",
        notes: data.notes || "",
        linkedCaseId: editingForm.caseId || ""
      });
      setBrandingContext(editingForm.brandingContext || "studio");
      if (editingForm.caseId) {
        setBrandingLocked(true);
      }
    }
  }, [editingForm]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.deleganteName.trim()) newErrors.deleganteName = "Campo obbligatorio";
    if (!formData.deleganteTaxCode.trim()) newErrors.deleganteTaxCode = "Campo obbligatorio";
    if (!formData.delegatoName.trim()) newErrors.delegatoName = "Campo obbligatorio";
    if (!formData.delegaScope.trim()) newErrors.delegaScope = "Campo obbligatorio";
    if (!formData.delegaValidFrom) newErrors.delegaValidFrom = "Campo obbligatorio";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    setSaveSuccess(false);
  };

  // Handle linking to an existing case - auto-infer branding
  const handleCaseSelect = (caseId) => {
    if (!caseId) {
      setFormData(prev => ({ ...prev, linkedCaseId: "" }));
      setBrandingLocked(false);
      return;
    }
    
    const selectedCase = cases.find(c => c.id === caseId);
    if (selectedCase) {
      const inferredBranding = inferBrandingFromPracticeType(selectedCase.practiceType);
      const linkedClient = clients.find(c => c.id === selectedCase.clientId);
      
      setFormData(prev => ({
        ...prev,
        linkedCaseId: caseId,
        deleganteId: selectedCase.clientId || prev.deleganteId,
        deleganteName: linkedClient?.fullName || prev.deleganteName,
        deleganteTaxCode: linkedClient?.taxCode || prev.deleganteTaxCode,
        deleganteAddress: linkedClient?.address || prev.deleganteAddress
      }));
      
      setBrandingContext(inferredBranding);
      setBrandingLocked(true);
    }
    setSaveSuccess(false);
  };

  const handleClientSelect = (clientName) => {
    const client = clients.find(c => c.fullName === clientName);
    if (client) {
      setFormData(prev => ({
        ...prev,
        deleganteName: client.fullName,
        deleganteId: client.id,
        deleganteTaxCode: client.taxCode || "",
        deleganteAddress: client.address || ""
      }));
    } else {
      updateField("deleganteName", clientName);
      updateField("deleganteId", "");
    }
    setSaveSuccess(false);
  };

  const handleSave = async (andPrint = false) => {
    if (!validateForm()) return;
    
    try {
      setIsSaving(true);
      
      const linkedCase = formData.linkedCaseId ? cases.find(c => c.id === formData.linkedCaseId) : null;
      
      const saveData = {
        formType: "delega",
        formName: `Delega - ${formData.deleganteName}`,
        clientId: formData.deleganteId || null,
        clientName: formData.deleganteName,
        caseId: formData.linkedCaseId || null,
        caseNumber: linkedCase?.caseNumber || null,
        operatorId: formData.operatorId || null,
        operatorName: formData.operator,
        brandingContext: brandingContext,
        formData: { ...formData }
      };
      
      let savedForm;
      if (editingForm) {
        savedForm = await api.updateForm(editingForm.id, saveData);
      } else {
        savedForm = await api.createForm(saveData);
      }
      
      setSaveSuccess(true);
      
      if (andPrint) {
        await api.downloadFormPDF(savedForm.id);
      }
      
      setTimeout(() => onSaved(), 1000);
      
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  const delegaScopeOptions = [
    "Ritiro documenti",
    "Presentazione istanze",
    "Firma documenti",
    "Operazioni bancarie",
    "Pratiche amministrative",
    "Pratiche previdenziali (INPS/INAIL)",
    "Altro"
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} data-testid="back-to-forms-button">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai Moduli
        </Button>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {editingForm ? "Modifica Delega" : "Delega"}
          </h2>
          <p className="text-sm text-slate-500">Autorizzazione per delega a terzi</p>
        </div>
      </div>

      <Card className="shadow-md">
        <CardHeader className="border-b">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <UserCheck className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Modulo di Delega</CardTitle>
              <CardDescription>Autorizzazione a terzi</CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* DELEGANTE */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <User className="h-4 w-4" />
              Dati del Delegante (Chi delega)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome e Cognome / Ragione Sociale *</Label>
                <Input
                  value={formData.deleganteName}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  placeholder="Es. Mario Rossi"
                  className={errors.deleganteName ? "border-red-500" : ""}
                  list="client-list-delega"
                  data-testid="delega-delegante-name"
                />
                <datalist id="client-list-delega">
                  {clients.map(c => <option key={c.id} value={c.fullName} />)}
                </datalist>
                {errors.deleganteName && <p className="text-xs text-red-500 mt-1">{errors.deleganteName}</p>}
              </div>
              <div>
                <Label>Codice Fiscale *</Label>
                <Input
                  value={formData.deleganteTaxCode}
                  onChange={(e) => updateField("deleganteTaxCode", e.target.value)}
                  placeholder="Es. RSSMRA80A01H501X"
                  className={errors.deleganteTaxCode ? "border-red-500" : ""}
                  data-testid="delega-delegante-cf"
                />
                {errors.deleganteTaxCode && <p className="text-xs text-red-500 mt-1">{errors.deleganteTaxCode}</p>}
              </div>
              <div>
                <Label>Data di Nascita</Label>
                <Input
                  type="date"
                  value={formData.deleganteBirthDate}
                  onChange={(e) => updateField("deleganteBirthDate", e.target.value)}
                />
              </div>
              <div>
                <Label>Luogo di Nascita</Label>
                <Input
                  value={formData.deleganteBirthPlace}
                  onChange={(e) => updateField("deleganteBirthPlace", e.target.value)}
                  placeholder="Es. Roma (RM)"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Indirizzo di Residenza</Label>
                <Input
                  value={formData.deleganteAddress}
                  onChange={(e) => updateField("deleganteAddress", e.target.value)}
                  placeholder="Es. Via Roma, 123 - 00100 Roma"
                />
              </div>
              <div>
                <Label>Tipo Documento</Label>
                <Select value={formData.deleganteDocument} onValueChange={(v) => updateField("deleganteDocument", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona documento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Carta d'Identità">Carta d'Identità</SelectItem>
                    <SelectItem value="Patente">Patente</SelectItem>
                    <SelectItem value="Passaporto">Passaporto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Numero Documento</Label>
                <Input
                  value={formData.deleganteDocumentNumber}
                  onChange={(e) => updateField("deleganteDocumentNumber", e.target.value)}
                  placeholder="Es. AX1234567"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* DELEGATO */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Dati del Delegato (Chi riceve la delega)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome e Cognome *</Label>
                <Input
                  value={formData.delegatoName}
                  onChange={(e) => updateField("delegatoName", e.target.value)}
                  placeholder="Es. Giuseppe Verdi"
                  className={errors.delegatoName ? "border-red-500" : ""}
                  data-testid="delega-delegato-name"
                />
                {errors.delegatoName && <p className="text-xs text-red-500 mt-1">{errors.delegatoName}</p>}
              </div>
              <div>
                <Label>Codice Fiscale</Label>
                <Input
                  value={formData.delegatoTaxCode}
                  onChange={(e) => updateField("delegatoTaxCode", e.target.value)}
                  placeholder="Es. VRDGPP85B01H501Y"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Relazione con il Delegante</Label>
                <Input
                  value={formData.delegatoRelation}
                  onChange={(e) => updateField("delegatoRelation", e.target.value)}
                  placeholder="Es. Figlio, Coniuge, Collaboratore, Legale rappresentante..."
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* OGGETTO DELEGA */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <FileSignature className="h-4 w-4" />
              Oggetto della Delega
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tipologia Delega *</Label>
                <Select value={formData.delegaScope} onValueChange={(v) => updateField("delegaScope", v)}>
                  <SelectTrigger className={errors.delegaScope ? "border-red-500" : ""} data-testid="delega-scope">
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    {delegaScopeOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.delegaScope && <p className="text-xs text-red-500 mt-1">{errors.delegaScope}</p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Valida dal *</Label>
                  <Input
                    type="date"
                    value={formData.delegaValidFrom}
                    onChange={(e) => updateField("delegaValidFrom", e.target.value)}
                    className={errors.delegaValidFrom ? "border-red-500" : ""}
                  />
                </div>
                <div>
                  <Label>Valida fino al</Label>
                  <Input
                    type="date"
                    value={formData.delegaValidTo}
                    onChange={(e) => updateField("delegaValidTo", e.target.value)}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Descrizione dettagliata</Label>
                <Textarea
                  value={formData.delegaDescription}
                  onChange={(e) => updateField("delegaDescription", e.target.value)}
                  placeholder="Descrivere in dettaglio le operazioni per cui si conferisce delega..."
                  rows={3}
                  data-testid="delega-description"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* OPERATORE */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Operatore / Studio
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Operatore</Label>
                <Input
                  value={formData.operator}
                  onChange={(e) => updateField("operator", e.target.value)}
                  placeholder="Es. Dott. Bianchi"
                  list="operator-list-delega"
                />
                <datalist id="operator-list-delega">
                  {operators.filter(op => op.status !== "sospeso").map(o => <option key={o.id} value={o.fullName} />)}
                </datalist>
              </div>
              <div>
                <Label>Note</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Eventuali note..."
                />
              </div>
              {/* Link to existing practice - STABLE */}
              <div className={cases && cases.length > 0 ? "md:col-span-2" : "hidden"}>
                {cases && cases.length > 0 ? (
                  <div>
                    <Label>Collega a Pratica Esistente (opzionale)</Label>
                    <Select value={formData.linkedCaseId || "none"} onValueChange={(v) => handleCaseSelect(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="link-case-select-delega">
                        <SelectValue placeholder="Seleziona pratica..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuna pratica collegata —</SelectItem>
                        {cases.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.caseNumber} - {c.subject?.substring(0, 40) || 'N/D'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InfoMessage show={formData.linkedCaseId}>
                      <p className="text-xs text-teal-600 mt-1">
                        Il branding verrà impostato automaticamente in base al tipo pratica
                      </p>
                    </InfoMessage>
                  </div>
                ) : null}
              </div>
              <div className="md:col-span-2">
                <Label>Intestazione Documento (Branding)</Label>
                <InfoMessage show={brandingLocked} className="mb-2">
                  <p className="text-xs text-amber-600">
                    Branding determinato dal tipo pratica collegata
                  </p>
                </InfoMessage>
                <div className="flex gap-4 mt-2">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'studio' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input type="radio" name="branding-delega" value="studio" checked={brandingContext === 'studio'} onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)} disabled={brandingLocked} className="sr-only" />
                    <Building2 className={`h-5 w-5 ${brandingContext === 'studio' ? 'text-teal-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${brandingContext === 'studio' ? 'text-teal-700' : 'text-slate-600'}`}>Gestione Studio</p>
                      <p className="text-xs text-slate-500">Logo e intestazione Studio</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'agenzia' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input type="radio" name="branding-delega" value="agenzia" checked={brandingContext === 'agenzia'} onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)} disabled={brandingLocked} className="sr-only" />
                    <Briefcase className={`h-5 w-5 ${brandingContext === 'agenzia' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${brandingContext === 'agenzia' ? 'text-orange-700' : 'text-slate-600'}`}>Gestione Agenzia</p>
                      <p className="text-xs text-slate-500">Logo Prisma Servizi</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Signature Preview */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Area Firme (nel PDF)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-slate-600 mb-2">Firma del Delegante</p>
                <div className="border-b-2 border-slate-300 h-12"></div>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-2">Firma del Delegato (per accettazione)</p>
                <div className="border-b-2 border-slate-300 h-12"></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <div className="flex items-center gap-2 mr-auto min-h-[28px]">
          <div className={`flex items-center gap-2 text-green-600 ${saveSuccess ? '' : 'hidden'}`}>
            <CheckCircle2 className="h-5 w-5" />
            <span>Modulo salvato con successo!</span>
          </div>
        </div>
        <Button variant="outline" onClick={onBack}>Annulla</Button>
        <Button variant="outline" onClick={() => handleSave(false)} disabled={isSaving} data-testid="save-delega-button">
          <Save className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva
        </Button>
        <Button onClick={() => handleSave(true)} disabled={isSaving} className="bg-teal-600 hover:bg-teal-700">
          <Printer className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva e Scarica PDF
        </Button>
      </div>
    </div>
  );
}

// ==================== INCARICO PROFESSIONALE FORM ====================
function IncaricoProfessionaleForm({ editingForm, clients, operators, cases, currentOperator, onBack, onSaved }) {
  const [formData, setFormData] = useState({
    // Cliente
    clientName: "",
    clientId: "",
    clientTaxCode: "",
    clientAddress: "",
    clientEmail: "",
    clientPhone: "",
    // Incarico
    incaricoDate: new Date().toISOString().split("T")[0],
    incaricoType: "",
    incaricoDescription: "",
    incaricoStartDate: new Date().toISOString().split("T")[0],
    incaricoEndDate: "",
    incaricoDuration: "Annuale",
    // Compenso
    compensoType: "Fisso",
    compensoAmount: "",
    compensoFrequency: "Mensile",
    compensoNotes: "",
    // Operatore
    operator: currentOperator?.fullName || "",
    operatorId: currentOperator?.id || "",
    notes: "",
    // Practice link
    linkedCaseId: ""
  });
  const [brandingContext, setBrandingContext] = useState("studio");
  const [brandingLocked, setBrandingLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (editingForm) {
      const data = editingForm.formData || {};
      setFormData({
        clientName: data.clientName || editingForm.clientName || "",
        clientId: editingForm.clientId || "",
        clientTaxCode: data.clientTaxCode || "",
        clientAddress: data.clientAddress || "",
        clientEmail: data.clientEmail || "",
        clientPhone: data.clientPhone || "",
        incaricoDate: data.incaricoDate || new Date().toISOString().split("T")[0],
        incaricoType: data.incaricoType || "",
        incaricoDescription: data.incaricoDescription || "",
        incaricoStartDate: data.incaricoStartDate || new Date().toISOString().split("T")[0],
        incaricoEndDate: data.incaricoEndDate || "",
        incaricoDuration: data.incaricoDuration || "Annuale",
        compensoType: data.compensoType || "Fisso",
        compensoAmount: data.compensoAmount || "",
        compensoFrequency: data.compensoFrequency || "Mensile",
        compensoNotes: data.compensoNotes || "",
        operator: data.operator || editingForm.operatorName || "",
        operatorId: editingForm.operatorId || "",
        notes: data.notes || "",
        linkedCaseId: editingForm.caseId || ""
      });
      setBrandingContext(editingForm.brandingContext || "studio");
      if (editingForm.caseId) {
        setBrandingLocked(true);
      }
    }
  }, [editingForm]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.clientName.trim()) newErrors.clientName = "Campo obbligatorio";
    if (!formData.clientTaxCode.trim()) newErrors.clientTaxCode = "Campo obbligatorio";
    if (!formData.incaricoType.trim()) newErrors.incaricoType = "Campo obbligatorio";
    if (!formData.incaricoDescription.trim()) newErrors.incaricoDescription = "Campo obbligatorio";
    if (!formData.compensoAmount.trim()) newErrors.compensoAmount = "Campo obbligatorio";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    setSaveSuccess(false);
  };

  // Handle linking to an existing case - auto-infer branding
  const handleCaseSelect = (caseId) => {
    if (!caseId) {
      setFormData(prev => ({ ...prev, linkedCaseId: "" }));
      setBrandingLocked(false);
      return;
    }
    
    const selectedCase = cases.find(c => c.id === caseId);
    if (selectedCase) {
      const inferredBranding = inferBrandingFromPracticeType(selectedCase.practiceType);
      const linkedClient = clients.find(c => c.id === selectedCase.clientId);
      
      setFormData(prev => ({
        ...prev,
        linkedCaseId: caseId,
        clientId: selectedCase.clientId || prev.clientId,
        clientName: linkedClient?.fullName || prev.clientName,
        clientTaxCode: linkedClient?.taxCode || prev.clientTaxCode,
        clientAddress: linkedClient?.address || prev.clientAddress,
        clientEmail: linkedClient?.email || prev.clientEmail,
        clientPhone: linkedClient?.phone || prev.clientPhone
      }));
      
      setBrandingContext(inferredBranding);
      setBrandingLocked(true);
    }
    setSaveSuccess(false);
  };

  const handleClientSelect = (clientName) => {
    const client = clients.find(c => c.fullName === clientName);
    if (client) {
      setFormData(prev => ({
        ...prev,
        clientName: client.fullName,
        clientId: client.id,
        clientTaxCode: client.taxCode || "",
        clientAddress: client.address || "",
        clientEmail: client.email || "",
        clientPhone: client.phone || ""
      }));
    } else {
      updateField("clientName", clientName);
      updateField("clientId", "");
    }
    setSaveSuccess(false);
  };

  const handleSave = async (andPrint = false) => {
    if (!validateForm()) return;
    
    try {
      setIsSaving(true);
      
      const linkedCase = formData.linkedCaseId ? cases.find(c => c.id === formData.linkedCaseId) : null;
      
      const saveData = {
        formType: "incarico-professionale",
        formName: `Incarico Professionale - ${formData.clientName}`,
        clientId: formData.clientId || null,
        clientName: formData.clientName,
        caseId: formData.linkedCaseId || null,
        caseNumber: linkedCase?.caseNumber || null,
        operatorId: formData.operatorId || null,
        operatorName: formData.operator,
        brandingContext: brandingContext,
        formData: { ...formData }
      };
      
      let savedForm;
      if (editingForm) {
        savedForm = await api.updateForm(editingForm.id, saveData);
      } else {
        savedForm = await api.createForm(saveData);
      }
      
      setSaveSuccess(true);
      
      if (andPrint) {
        await api.downloadFormPDF(savedForm.id);
      }
      
      setTimeout(() => onSaved(), 1000);
      
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  const incaricoTypeOptions = [
    "Elaborazione paghe e contributi",
    "Consulenza del lavoro",
    "Gestione amministrativa del personale",
    "Assistenza contrattuale",
    "Assistenza previdenziale",
    "Vertenze e contenzioso",
    "Altro"
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} data-testid="back-to-forms-button">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai Moduli
        </Button>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            {editingForm ? "Modifica Incarico" : "Incarico Professionale"}
          </h2>
          <p className="text-sm text-slate-500">Conferimento incarico di consulenza</p>
        </div>
      </div>

      <Card className="shadow-md">
        <CardHeader className="border-b">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <Briefcase className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Conferimento Incarico Professionale</CardTitle>
              <CardDescription>Consulenza del Lavoro</CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* CLIENTE */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <User className="h-4 w-4" />
              Dati del Cliente / Committente
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome / Ragione Sociale *</Label>
                <Input
                  value={formData.clientName}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  placeholder="Es. Azienda Esempio Srl"
                  className={errors.clientName ? "border-red-500" : ""}
                  list="client-list-incarico"
                  data-testid="incarico-client-name"
                />
                <datalist id="client-list-incarico">
                  {clients.map(c => <option key={c.id} value={c.fullName} />)}
                </datalist>
                {errors.clientName && <p className="text-xs text-red-500 mt-1">{errors.clientName}</p>}
              </div>
              <div>
                <Label>Codice Fiscale / P.IVA *</Label>
                <Input
                  value={formData.clientTaxCode}
                  onChange={(e) => updateField("clientTaxCode", e.target.value)}
                  placeholder="Es. IT01234567890"
                  className={errors.clientTaxCode ? "border-red-500" : ""}
                  data-testid="incarico-client-cf"
                />
                {errors.clientTaxCode && <p className="text-xs text-red-500 mt-1">{errors.clientTaxCode}</p>}
              </div>
              <div className="md:col-span-2">
                <Label>Sede Legale</Label>
                <Input
                  value={formData.clientAddress}
                  onChange={(e) => updateField("clientAddress", e.target.value)}
                  placeholder="Es. Via Roma, 123 - 00100 Roma"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) => updateField("clientEmail", e.target.value)}
                  placeholder="Es. info@azienda.it"
                />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input
                  value={formData.clientPhone}
                  onChange={(e) => updateField("clientPhone", e.target.value)}
                  placeholder="Es. +39 06 1234567"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* INCARICO */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Oggetto dell'Incarico
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Data Incarico</Label>
                <Input
                  type="date"
                  value={formData.incaricoDate}
                  onChange={(e) => updateField("incaricoDate", e.target.value)}
                />
              </div>
              <div>
                <Label>Tipologia Incarico *</Label>
                <Select value={formData.incaricoType} onValueChange={(v) => updateField("incaricoType", v)}>
                  <SelectTrigger className={errors.incaricoType ? "border-red-500" : ""} data-testid="incarico-type">
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    {incaricoTypeOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.incaricoType && <p className="text-xs text-red-500 mt-1">{errors.incaricoType}</p>}
              </div>
              <div className="md:col-span-2">
                <Label>Descrizione Prestazioni *</Label>
                <Textarea
                  value={formData.incaricoDescription}
                  onChange={(e) => updateField("incaricoDescription", e.target.value)}
                  placeholder="Descrivere le prestazioni oggetto dell'incarico..."
                  rows={4}
                  className={errors.incaricoDescription ? "border-red-500" : ""}
                  data-testid="incarico-description"
                />
                {errors.incaricoDescription && <p className="text-xs text-red-500 mt-1">{errors.incaricoDescription}</p>}
              </div>
              <div>
                <Label>Decorrenza</Label>
                <Input
                  type="date"
                  value={formData.incaricoStartDate}
                  onChange={(e) => updateField("incaricoStartDate", e.target.value)}
                />
              </div>
              <div>
                <Label>Durata</Label>
                <Select value={formData.incaricoDuration} onValueChange={(v) => updateField("incaricoDuration", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Annuale">Annuale (rinnovo tacito)</SelectItem>
                    <SelectItem value="Biennale">Biennale</SelectItem>
                    <SelectItem value="A tempo indeterminato">A tempo indeterminato</SelectItem>
                    <SelectItem value="Una tantum">Una tantum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* COMPENSO */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Condizioni Economiche
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Tipo Compenso</Label>
                <Select value={formData.compensoType} onValueChange={(v) => updateField("compensoType", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fisso">Fisso</SelectItem>
                    <SelectItem value="A ore">A ore</SelectItem>
                    <SelectItem value="A prestazione">A prestazione</SelectItem>
                    <SelectItem value="Percentuale">Percentuale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Importo (EUR) *</Label>
                <Input
                  type="number"
                  value={formData.compensoAmount}
                  onChange={(e) => updateField("compensoAmount", e.target.value)}
                  placeholder="Es. 500.00"
                  className={errors.compensoAmount ? "border-red-500" : ""}
                  data-testid="incarico-compenso"
                />
                {errors.compensoAmount && <p className="text-xs text-red-500 mt-1">{errors.compensoAmount}</p>}
              </div>
              <div>
                <Label>Periodicità</Label>
                <Select value={formData.compensoFrequency} onValueChange={(v) => updateField("compensoFrequency", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mensile">Mensile</SelectItem>
                    <SelectItem value="Trimestrale">Trimestrale</SelectItem>
                    <SelectItem value="Semestrale">Semestrale</SelectItem>
                    <SelectItem value="Annuale">Annuale</SelectItem>
                    <SelectItem value="Una tantum">Una tantum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label>Note sul compenso</Label>
                <Input
                  value={formData.compensoNotes}
                  onChange={(e) => updateField("compensoNotes", e.target.value)}
                  placeholder="Es. IVA esclusa, pagamento a 30 giorni..."
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* OPERATORE */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Professionista / Studio
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Consulente del Lavoro</Label>
                <Input
                  value={formData.operator}
                  onChange={(e) => updateField("operator", e.target.value)}
                  placeholder="Es. Dott. Bianchi"
                  list="operator-list-incarico"
                />
                <datalist id="operator-list-incarico">
                  {operators.filter(op => op.status !== "sospeso").map(o => <option key={o.id} value={o.fullName} />)}
                </datalist>
              </div>
              <div>
                <Label>Note</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Eventuali note..."
                />
              </div>
              {/* Link to existing practice - STABLE */}
              <div className={cases && cases.length > 0 ? "md:col-span-2" : "hidden"}>
                {cases && cases.length > 0 ? (
                  <div>
                    <Label>Collega a Pratica Esistente (opzionale)</Label>
                    <Select value={formData.linkedCaseId || "none"} onValueChange={(v) => handleCaseSelect(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="link-case-select-incarico">
                        <SelectValue placeholder="Seleziona pratica..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuna pratica collegata —</SelectItem>
                        {cases.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.caseNumber} - {c.subject?.substring(0, 40) || 'N/D'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InfoMessage show={formData.linkedCaseId}>
                      <p className="text-xs text-teal-600 mt-1">
                        Il branding verrà impostato automaticamente in base al tipo pratica
                      </p>
                    </InfoMessage>
                  </div>
                ) : null}
              </div>
              <div className="md:col-span-2">
                <Label>Intestazione Documento (Branding)</Label>
                <InfoMessage show={brandingLocked} className="mb-2">
                  <p className="text-xs text-amber-600">
                    Branding determinato dal tipo pratica collegata
                  </p>
                </InfoMessage>
                <div className="flex gap-4 mt-2">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'studio' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input type="radio" name="branding-incarico" value="studio" checked={brandingContext === 'studio'} onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)} disabled={brandingLocked} className="sr-only" />
                    <Building2 className={`h-5 w-5 ${brandingContext === 'studio' ? 'text-teal-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${brandingContext === 'studio' ? 'text-teal-700' : 'text-slate-600'}`}>Gestione Studio</p>
                      <p className="text-xs text-slate-500">Logo e intestazione Studio</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'agenzia' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input type="radio" name="branding-incarico" value="agenzia" checked={brandingContext === 'agenzia'} onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)} disabled={brandingLocked} className="sr-only" />
                    <Briefcase className={`h-5 w-5 ${brandingContext === 'agenzia' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${brandingContext === 'agenzia' ? 'text-orange-700' : 'text-slate-600'}`}>Gestione Agenzia</p>
                      <p className="text-xs text-slate-500">Logo Prisma Servizi</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Signature Preview */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Area Firme (nel PDF)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-slate-600 mb-2">Firma del Cliente / Committente</p>
                <div className="border-b-2 border-slate-300 h-12"></div>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-2">Firma del Consulente del Lavoro</p>
                <div className="border-b-2 border-slate-300 h-12"></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <div className="flex items-center gap-2 mr-auto min-h-[28px]">
          <div className={`flex items-center gap-2 text-green-600 ${saveSuccess ? '' : 'hidden'}`}>
            <CheckCircle2 className="h-5 w-5" />
            <span>Modulo salvato con successo!</span>
          </div>
        </div>
        <Button variant="outline" onClick={onBack}>Annulla</Button>
        <Button variant="outline" onClick={() => handleSave(false)} disabled={isSaving} data-testid="save-incarico-button">
          <Save className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva
        </Button>
        <Button onClick={() => handleSave(true)} disabled={isSaving} className="bg-teal-600 hover:bg-teal-700">
          <Printer className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva e Scarica PDF
        </Button>
      </div>
    </div>
  );
}


// ==================== CONSENSO PRIVACY FORM ====================
// Testo informativa privacy GDPR-compliant
const PRIVACY_INFO_TEXT = `Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003, La informiamo che i Suoi dati personali saranno trattati con le seguenti modalità e finalità:

1. TITOLARE DEL TRATTAMENTO
Il Titolare del trattamento è lo Studio indicato in intestazione, nella persona del legale rappresentante pro tempore.

2. FINALITÀ DEL TRATTAMENTO
I dati personali da Lei forniti saranno utilizzati per:
• Adempimento degli obblighi contrattuali e precontrattuali
• Adempimento degli obblighi di legge (fiscali, contabili, amministrativi)
• Gestione delle pratiche di consulenza del lavoro

3. BASE GIURIDICA
Il trattamento dei dati è necessario per l'esecuzione del contratto di cui Lei è parte e per adempiere ad obblighi di legge.

4. CONSERVAZIONE DEI DATI
I dati saranno conservati per il tempo necessario all'espletamento delle finalità sopra indicate e comunque non oltre i termini di legge.

5. DIRITTI DELL'INTERESSATO
Lei ha diritto di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento, nonché il diritto di proporre reclamo all'Autorità Garante.`;

function ConsensoPrivacyForm({ editingForm, clients, operators, cases, currentOperator, onBack, onSaved }) {
  const [formData, setFormData] = useState({
    // Cliente
    clientName: "",
    clientId: "",
    clientTaxCode: "",
    clientAddress: "",
    // Consensi
    consentoTrattamentoDati: false, // Required
    consentoComunicazioniPromozionali: false, // Optional
    consentoComunicazioneTerzi: false, // Optional
    // Operatore
    operator: currentOperator?.fullName || "",
    operatorId: currentOperator?.id || "",
    consentDate: new Date().toISOString().split("T")[0],
    notes: "",
    // Practice link
    linkedCaseId: ""
  });
  const [brandingContext, setBrandingContext] = useState("studio");
  const [brandingLocked, setBrandingLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (editingForm) {
      const data = editingForm.formData || {};
      setFormData({
        clientName: data.clientName || editingForm.clientName || "",
        clientId: editingForm.clientId || "",
        clientTaxCode: data.clientTaxCode || "",
        clientAddress: data.clientAddress || "",
        consentoTrattamentoDati: data.consentoTrattamentoDati || false,
        consentoComunicazioniPromozionali: data.consentoComunicazioniPromozionali || false,
        consentoComunicazioneTerzi: data.consentoComunicazioneTerzi || false,
        operator: data.operator || editingForm.operatorName || "",
        operatorId: editingForm.operatorId || "",
        consentDate: data.consentDate || new Date().toISOString().split("T")[0],
        notes: data.notes || "",
        linkedCaseId: editingForm.caseId || ""
      });
      setBrandingContext(editingForm.brandingContext || "studio");
      if (editingForm.caseId) {
        setBrandingLocked(true);
      }
    }
  }, [editingForm]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.clientName.trim()) newErrors.clientName = "Campo obbligatorio";
    if (!formData.clientTaxCode.trim()) newErrors.clientTaxCode = "Campo obbligatorio";
    if (!formData.consentoTrattamentoDati) newErrors.consentoTrattamentoDati = "Il consenso al trattamento dati è obbligatorio";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    setSaveSuccess(false);
  };

  // Handle linking to an existing case - auto-infer branding
  const handleCaseSelect = (caseId) => {
    if (!caseId) {
      setFormData(prev => ({ ...prev, linkedCaseId: "" }));
      setBrandingLocked(false);
      return;
    }
    
    const selectedCase = cases.find(c => c.id === caseId);
    if (selectedCase) {
      const inferredBranding = inferBrandingFromPracticeType(selectedCase.practiceType);
      const linkedClient = clients.find(c => c.id === selectedCase.clientId);
      
      setFormData(prev => ({
        ...prev,
        linkedCaseId: caseId,
        clientId: selectedCase.clientId || prev.clientId,
        clientName: linkedClient?.fullName || prev.clientName,
        clientTaxCode: linkedClient?.taxCode || prev.clientTaxCode,
        clientAddress: linkedClient?.address || prev.clientAddress
      }));
      
      setBrandingContext(inferredBranding);
      setBrandingLocked(true);
    }
    setSaveSuccess(false);
  };

  const handleClientSelect = (clientName) => {
    const client = clients.find(c => c.fullName === clientName);
    if (client) {
      setFormData(prev => ({
        ...prev,
        clientName: client.fullName,
        clientId: client.id,
        clientTaxCode: client.taxCode || "",
        clientAddress: client.address || ""
      }));
    } else {
      updateField("clientName", clientName);
      updateField("clientId", "");
    }
    setSaveSuccess(false);
  };

  const handleSave = async (andPrint = false) => {
    if (!validateForm()) return;
    
    try {
      setIsSaving(true);
      
      const linkedCase = formData.linkedCaseId ? cases.find(c => c.id === formData.linkedCaseId) : null;
      
      const saveData = {
        formType: "consenso-privacy",
        formName: `Consenso Privacy - ${formData.clientName}`,
        clientId: formData.clientId || null,
        clientName: formData.clientName,
        caseId: formData.linkedCaseId || null,
        caseNumber: linkedCase?.caseNumber || null,
        operatorId: formData.operatorId || null,
        operatorName: formData.operator,
        brandingContext: brandingContext,
        formData: { ...formData }
      };
      
      let savedForm;
      if (editingForm) {
        savedForm = await api.updateForm(editingForm.id, saveData);
      } else {
        savedForm = await api.createForm(saveData);
      }
      
      setSaveSuccess(true);
      
      if (andPrint && savedForm?.id) {
        await api.downloadFormPDF(savedForm.id);
      }
      
      setTimeout(() => {
        onSaved();
      }, 1000);
      
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Indietro
        </Button>
        <div>
          <h2 className="text-xl font-bold text-slate-800">Consenso Privacy</h2>
          <p className="text-sm text-slate-500">Informativa e raccolta consensi GDPR</p>
        </div>
      </div>

      {/* Form Card */}
      <Card className="shadow-md">
        <CardContent className="p-6 space-y-6">
          {/* DATI CLIENTE */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <User className="h-4 w-4" />
              Dati dell'Interessato
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome / Ragione Sociale *</Label>
                <Input
                  value={formData.clientName}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  placeholder="Es. Mario Rossi / Azienda SRL"
                  list="client-list-privacy"
                  className={errors.clientName ? "border-red-500" : ""}
                  data-testid="privacy-client-name"
                />
                <datalist id="client-list-privacy">
                  {clients.map(c => <option key={c.id} value={c.fullName} />)}
                </datalist>
                {errors.clientName && <p className="text-xs text-red-500 mt-1">{errors.clientName}</p>}
              </div>
              <div>
                <Label>Codice Fiscale / P.IVA *</Label>
                <Input
                  value={formData.clientTaxCode}
                  onChange={(e) => updateField("clientTaxCode", e.target.value)}
                  placeholder="Es. RSSMRA80A01H501X"
                  className={errors.clientTaxCode ? "border-red-500" : ""}
                  data-testid="privacy-client-cf"
                />
                {errors.clientTaxCode && <p className="text-xs text-red-500 mt-1">{errors.clientTaxCode}</p>}
              </div>
              <div className="md:col-span-2">
                <Label>Indirizzo</Label>
                <Input
                  value={formData.clientAddress}
                  onChange={(e) => updateField("clientAddress", e.target.value)}
                  placeholder="Es. Via Roma 123, 00100 Roma (RM)"
                  data-testid="privacy-client-address"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* INFORMATIVA PRIVACY */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Informativa sul Trattamento dei Dati Personali
            </h3>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-64 overflow-y-auto">
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                {PRIVACY_INFO_TEXT}
              </pre>
            </div>
          </div>

          <Separator />

          {/* CONSENSI */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Manifestazione del Consenso
            </h3>
            <div className="space-y-4">
              {/* Consenso obbligatorio */}
              <div className={`p-4 rounded-lg border-2 ${formData.consentoTrattamentoDati ? 'border-green-500 bg-green-50' : errors.consentoTrattamentoDati ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consentoTrattamentoDati}
                    onChange={(e) => updateField("consentoTrattamentoDati", e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                    data-testid="consent-required"
                  />
                  <div>
                    <p className="font-medium text-slate-700">
                      Trattamento dati per finalità contrattuali <span className="text-red-500">*</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Presto il consenso al trattamento dei miei dati personali per le finalità contrattuali e gli adempimenti di legge indicati nell'informativa.
                    </p>
                  </div>
                </label>
                {errors.consentoTrattamentoDati && <p className="text-xs text-red-500 mt-2 ml-8">{errors.consentoTrattamentoDati}</p>}
              </div>

              {/* Consenso opzionale - comunicazioni promozionali */}
              <div className={`p-4 rounded-lg border-2 ${formData.consentoComunicazioniPromozionali ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consentoComunicazioniPromozionali}
                    onChange={(e) => updateField("consentoComunicazioniPromozionali", e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="consent-marketing"
                  />
                  <div>
                    <p className="font-medium text-slate-700">
                      Comunicazioni promozionali <span className="text-slate-400 text-sm">(facoltativo)</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Presto il consenso a ricevere comunicazioni informative e promozionali relative ai servizi dello Studio.
                    </p>
                  </div>
                </label>
              </div>

              {/* Consenso opzionale - comunicazione a terzi */}
              <div className={`p-4 rounded-lg border-2 ${formData.consentoComunicazioneTerzi ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consentoComunicazioneTerzi}
                    onChange={(e) => updateField("consentoComunicazioneTerzi", e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="consent-third-party"
                  />
                  <div>
                    <p className="font-medium text-slate-700">
                      Comunicazione a terzi <span className="text-slate-400 text-sm">(facoltativo)</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Presto il consenso alla comunicazione dei miei dati a soggetti terzi per finalità connesse all'erogazione dei servizi richiesti.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <Separator />

          {/* OPERATORE / STUDIO */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Operatore / Studio
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Operatore</Label>
                <Input
                  value={formData.operator}
                  onChange={(e) => updateField("operator", e.target.value)}
                  placeholder="Es. Marco Bianchi"
                  list="operator-list-privacy"
                />
                <datalist id="operator-list-privacy">
                  {operators.filter(op => op.status !== "sospeso").map(o => <option key={o.id} value={o.fullName} />)}
                </datalist>
              </div>
              <div>
                <Label>Data Consenso</Label>
                <Input
                  type="date"
                  value={formData.consentDate}
                  onChange={(e) => updateField("consentDate", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Note</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Eventuali note..."
                />
              </div>
              {/* Link to existing practice - STABLE */}
              <div className={cases && cases.length > 0 ? "md:col-span-2" : "hidden"}>
                {cases && cases.length > 0 ? (
                  <div>
                    <Label>Collega a Pratica Esistente (opzionale)</Label>
                    <Select value={formData.linkedCaseId || "none"} onValueChange={(v) => handleCaseSelect(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="link-case-select-privacy">
                        <SelectValue placeholder="Seleziona pratica..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Nessuna pratica collegata —</SelectItem>
                        {cases.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.caseNumber} - {c.subject?.substring(0, 40) || 'N/D'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InfoMessage show={formData.linkedCaseId}>
                      <p className="text-xs text-teal-600 mt-1">
                        Il branding verrà impostato automaticamente in base al tipo pratica
                      </p>
                    </InfoMessage>
                  </div>
                ) : null}
              </div>
              <div className="md:col-span-2">
                <Label>Intestazione Documento (Branding)</Label>
                <InfoMessage show={brandingLocked} className="mb-2">
                  <p className="text-xs text-amber-600">
                    Branding determinato dal tipo pratica collegata
                  </p>
                </InfoMessage>
                <div className="flex gap-4 mt-2">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'studio' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input type="radio" name="branding-privacy" value="studio" checked={brandingContext === 'studio'} onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)} disabled={brandingLocked} className="sr-only" />
                    <Building2 className={`h-5 w-5 ${brandingContext === 'studio' ? 'text-teal-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${brandingContext === 'studio' ? 'text-teal-700' : 'text-slate-600'}`}>Gestione Studio</p>
                      <p className="text-xs text-slate-500">Logo e intestazione Studio</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${brandingContext === 'agenzia' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input type="radio" name="branding-privacy" value="agenzia" checked={brandingContext === 'agenzia'} onChange={(e) => !brandingLocked && setBrandingContext(e.target.value)} disabled={brandingLocked} className="sr-only" />
                    <Briefcase className={`h-5 w-5 ${brandingContext === 'agenzia' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${brandingContext === 'agenzia' ? 'text-orange-700' : 'text-slate-600'}`}>Gestione Agenzia</p>
                      <p className="text-xs text-slate-500">Logo Prisma Servizi</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Signature Preview */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Area Firme (nel PDF)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-slate-600 mb-2">Firma dell'Interessato</p>
                <div className="border-b-2 border-slate-300 h-12"></div>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-2">Firma dell'Operatore</p>
                <div className="border-b-2 border-slate-300 h-12"></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <div className="flex items-center gap-2 mr-auto min-h-[28px]">
          <div className={`flex items-center gap-2 text-green-600 ${saveSuccess ? '' : 'hidden'}`}>
            <CheckCircle2 className="h-5 w-5" />
            <span>Modulo salvato con successo!</span>
          </div>
        </div>
        <Button variant="outline" onClick={onBack}>Annulla</Button>
        <Button variant="outline" onClick={() => handleSave(false)} disabled={isSaving} data-testid="save-privacy-button">
          <Save className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva
        </Button>
        <Button onClick={() => handleSave(true)} disabled={isSaving} className="bg-teal-600 hover:bg-teal-700">
          <Printer className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva e Scarica PDF
        </Button>
      </div>
    </div>
  );
}

// Wave 4.2 (01/07/2026) — Memoize il top-level component così App.js
// non forza la riconciliazione dell'albero (3k+ righe) ad ogni switch
// tab. Tutte le props sono stabili (useCallback in App.js Wave 4.2).
export default React.memo(Modulistica);

